import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import type { Equipment } from "../hooks/useEquipments"
import type {
  EquipmentIssue,
  EquipmentComment,
  IssueAttachment,
} from "../hooks/useEquipmentIssues"
import { ensureKoreanFont, safeFontName } from "./pdfKoreanFont"

/**
 * 장비 이슈 PDF 생성 공용 모듈.
 * - 전체(표지 + 이슈별 상세) 출력과 개별 이슈 1건 출력에서 동일 로직을 재사용한다.
 */

/** 실제 렌더링에 사용할 폰트명. 한글 폰트 로드 실패 시 helvetica 로 폴백된다. */
let KR_FONT = "NotoSansKR"

// ===== 페이지 상수 (A4 세로, mm) =====
const PAGE_WIDTH = 210
const PAGE_HEIGHT = 297
const MARGIN = 14
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2
const BOTTOM_LIMIT = PAGE_HEIGHT - MARGIN - 6

export const STATUS_LABEL: Record<string, string> = {
  issued: "이슈 등록",
  checking: "확인중",
  resolved: "조치완료",
  unavailable: "조치불가",
}

const STATUS_RGB: Record<string, [number, number, number]> = {
  issued: [239, 68, 68],
  checking: [245, 158, 11],
  resolved: [16, 185, 129],
  unavailable: [100, 116, 139],
}

/** 출력 정렬 기준: 이슈 등록 → 확인중 → 조치완료 → 조치불가 */
const STATUS_ORDER: Record<string, number> = {
  issued: 0,
  checking: 1,
  resolved: 2,
  unavailable: 3,
}

/** 상태 우선순위 정렬 + 동일 상태 내 등록일 오름차순 */
export function sortIssuesByStatus(list: EquipmentIssue[]): EquipmentIssue[] {
  return list.slice().sort((a, b) => {
    const oa = STATUS_ORDER[a.status] ?? 99
    const ob = STATUS_ORDER[b.status] ?? 99
    if (oa !== ob) return oa - ob
    return (a.createdAt || "").localeCompare(b.createdAt || "")
  })
}

const ORANGE: [number, number, number] = [245, 158, 11]
const RED: [number, number, number] = [239, 68, 68]
const GREEN: [number, number, number] = [16, 185, 129]
const BLUE: [number, number, number] = [59, 130, 246]

function fmtDate(value?: string) {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function fmtDateTime(value?: string) {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return `${fmtDate(value)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

function isImage(a: { url: string; name?: string; type?: string }) {
  if (a.type === "image") return true
  if (a.type && a.type.startsWith("image/")) return true
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(a.name || a.url || "")
}

// ===================================================================
// 이미지 로더 (다단계 폴백)
// -------------------------------------------------------------------
// 화면에서 <img src>로 먼저 표시된 이미지는 CORS 헤더 없이 캐시되기 때문에,
// 이후 동일 URL에 대한 fetch(mode:"cors")가 캐시된 응답을 재사용하면서
// 차단되어 PDF에 빈칸으로 출력되는 문제가 있었다.
// 아래 순서로 재시도하여 어떤 환경에서도 이미지를 확보한다.
//   1) fetch(cors) + cache:"reload"  → 캐시 무시하고 CORS 응답 재수신
//   2) fetch(cors) 기본 캐시
//   3) <img crossOrigin="anonymous">
//   4) <img crossOrigin="anonymous"> + 캐시버스터
//   5) 이미지 프록시(CORS 헤더 보장) 경유
// ===================================================================

export interface LoadedImage {
  data: string
  w: number
  h: number
}

/** 동일 URL 반복 로드를 막기 위한 세션 캐시 (실패 결과도 캐싱) */
const imageCache = new Map<string, LoadedImage | null>()

function blobToImage(blob: Blob): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!blob || blob.size === 0) {
      resolve(null)
      return
    }
    const objectUrl = URL.createObjectURL(blob)
    const im = new Image()
    im.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(im)
    }
    im.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(null)
    }
    im.src = objectUrl
  })
}

async function loadViaFetch(url: string, init: RequestInit): Promise<HTMLImageElement | null> {
  try {
    const res = await fetch(url, init)
    if (!res.ok) return null
    const blob = await res.blob()
    // HTML 오류 페이지가 내려오는 경우 방어
    if (blob.type && blob.type.startsWith("text/")) return null
    return await blobToImage(blob)
  } catch {
    return null
  }
}

function loadViaImgElement(url: string, useCors: boolean): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const im = new Image()
    if (useCors) im.crossOrigin = "anonymous"
    im.onload = () => resolve(im)
    im.onerror = () => resolve(null)
    im.src = url
  })
}

function withCacheBust(url: string) {
  return `${url}${url.includes("?") ? "&" : "?"}_pdfcb=${Date.now()}`
}

/** CORS 헤더를 보장하는 공개 이미지 프록시 (최후 폴백) */
function buildProxyUrl(url: string) {
  const stripped = url.replace(/^https?:\/\//, "")
  return `https://images.weserv.nl/?url=${encodeURIComponent(stripped)}&output=jpg&n=-1`
}

/** 캔버스로 리사이즈 후 JPEG 재인코딩. 캔버스가 오염된 경우 null */
function toJpeg(img: HTMLImageElement): LoadedImage | null {
  const MAX_EDGE = 1600
  let w = img.naturalWidth || img.width
  let h = img.naturalHeight || img.height
  if (!w || !h) return null
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h))
  w = Math.max(1, Math.round(w * scale))
  h = Math.max(1, Math.round(h * scale))
  const canvas = document.createElement("canvas")
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext("2d")
  if (!ctx) return null
  // 투명 배경(PNG) 대비 흰 바탕을 깔아 인쇄물에서 검게 보이는 문제 방지
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, w, h)
  ctx.drawImage(img, 0, 0, w, h)
  try {
    return { data: canvas.toDataURL("image/jpeg", 0.85), w, h }
  } catch {
    // 캔버스 오염(tainted) → 다음 전략으로
    return null
  }
}

export async function loadImageForPdf(url: string): Promise<LoadedImage | null> {
  if (!url) return null
  if (imageCache.has(url)) return imageCache.get(url) as LoadedImage | null

  const attempts: Array<() => Promise<HTMLImageElement | null>> = [
    () => loadViaFetch(url, { mode: "cors", credentials: "omit", cache: "reload" }),
    () => loadViaFetch(url, { mode: "cors", credentials: "omit" }),
    () => loadViaImgElement(url, true),
    () => loadViaImgElement(withCacheBust(url), true),
    () => loadViaFetch(buildProxyUrl(url), { mode: "cors", credentials: "omit" }),
  ]

  let result: LoadedImage | null = null
  for (const attempt of attempts) {
    const img = await attempt()
    if (!img) continue
    const jpeg = toJpeg(img)
    if (jpeg) {
      result = jpeg
      break
    }
  }
  if (!result) console.warn("PDF 첨부 이미지 로드 실패:", url)
  imageCache.set(url, result)
  return result
}

// ===== 그리기 헬퍼 =====
function ensureSpace(pdf: jsPDF, y: number, need: number): number {
  if (y + need > BOTTOM_LIMIT) {
    pdf.addPage()
    return MARGIN
  }
  return y
}

function drawParagraph(
  pdf: jsPDF,
  text: string,
  y: number,
  opts?: { size?: number; color?: [number, number, number]; indent?: number; bold?: boolean }
): number {
  const size = opts?.size ?? 9.5
  const color = opts?.color ?? ([55, 65, 81] as [number, number, number])
  const indent = opts?.indent ?? 0
  const body = (text || "").trim()
  if (!body) return y
  pdf.setFont(KR_FONT, opts?.bold ? "bold" : "normal")
  pdf.setFontSize(size)
  pdf.setTextColor(color[0], color[1], color[2])
  const lineHeight = size * 0.42 + 1.4
  const lines: string[] = pdf.splitTextToSize(body, CONTENT_WIDTH - indent)
  for (const line of lines) {
    y = ensureSpace(pdf, y, lineHeight)
    pdf.text(line, MARGIN + indent, y + lineHeight - 1.5)
    y += lineHeight
  }
  return y
}

// 구역(이슈 발생 / 조치 결과 / 코멘트) 제목 바
function drawSectionBar(
  pdf: jsPDF,
  label: string,
  y: number,
  rgb: [number, number, number]
): number {
  y = ensureSpace(pdf, y, 14)
  const barH = 7.5
  pdf.setFillColor(249, 250, 251)
  pdf.rect(MARGIN, y, CONTENT_WIDTH, barH, "F")
  pdf.setFillColor(rgb[0], rgb[1], rgb[2])
  pdf.rect(MARGIN, y, 2, barH, "F")
  pdf.setDrawColor(229, 231, 235)
  pdf.setLineWidth(0.2)
  pdf.rect(MARGIN, y, CONTENT_WIDTH, barH, "S")
  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(10)
  pdf.setTextColor(rgb[0], rgb[1], rgb[2])
  pdf.text(label, MARGIN + 4.5, y + 5.2)
  return y + barH + 3
}

// ===== 코멘트 카드 =====
const CARD_PAD_X = 4
const CARD_PAD_Y = 3.2
const CARD_ACCENT_W = 1.6

function drawCommentCard(
  pdf: jsPDF,
  headerText: string,
  contentText: string,
  y: number
): number {
  const headerSize = 8.5
  const contentSize = 9
  const headerLH = headerSize * 0.42 + 1.4
  const contentLH = contentSize * 0.42 + 1.4
  const textX = MARGIN + CARD_ACCENT_W + CARD_PAD_X
  const textW = CONTENT_WIDTH - CARD_ACCENT_W - CARD_PAD_X * 2

  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(headerSize)
  const headerLines: string[] = pdf.splitTextToSize(headerText || "-", textW)

  pdf.setFont(KR_FONT, "normal")
  pdf.setFontSize(contentSize)
  const body = (contentText || "").trim()
  const contentLines: string[] = body ? pdf.splitTextToSize(body, textW) : []

  const remaining = [...contentLines]
  let isFirstChunk = true
  const pageMax = BOTTOM_LIMIT - MARGIN - CARD_PAD_Y * 2

  for (;;) {
    const minNeed =
      (isFirstChunk ? headerLines.length * headerLH + 1 : 0) +
      (remaining.length > 0 ? contentLH : 0)
    let avail = BOTTOM_LIMIT - y - CARD_PAD_Y * 2
    if (avail < minNeed) {
      pdf.addPage()
      y = MARGIN
      avail = pageMax
    }

    let used = 0
    const chunkHeader = isFirstChunk ? headerLines : []
    if (isFirstChunk) used += headerLines.length * headerLH + 1
    const chunkLines: string[] = []
    while (remaining.length > 0 && used + contentLH <= avail) {
      chunkLines.push(remaining.shift() as string)
      used += contentLH
    }

    const boxH = used + CARD_PAD_Y * 2

    pdf.setFillColor(249, 250, 251)
    pdf.rect(MARGIN, y, CONTENT_WIDTH, boxH, "F")
    pdf.setDrawColor(226, 232, 240)
    pdf.setLineWidth(0.25)
    pdf.rect(MARGIN, y, CONTENT_WIDTH, boxH, "S")
    pdf.setFillColor(191, 219, 254)
    pdf.rect(MARGIN, y, CARD_ACCENT_W, boxH, "F")

    let ty = y + CARD_PAD_Y
    if (chunkHeader.length > 0) {
      pdf.setFont(KR_FONT, "bold")
      pdf.setFontSize(headerSize)
      pdf.setTextColor(37, 99, 235)
      for (const line of chunkHeader) {
        pdf.text(line, textX, ty + headerLH - 1.5)
        ty += headerLH
      }
      ty += 1
    }
    if (chunkLines.length > 0) {
      pdf.setFont(KR_FONT, "normal")
      pdf.setFontSize(contentSize)
      pdf.setTextColor(55, 65, 81)
      for (const line of chunkLines) {
        pdf.text(line, textX, ty + contentLH - 1.5)
        ty += contentLH
      }
    }

    y += boxH
    isFirstChunk = false
    if (remaining.length === 0) break
    pdf.addPage()
    y = MARGIN
  }

  return y
}

// 첨부 이미지 2열 그리드
async function drawImageGrid(
  pdf: jsPDF,
  attachments: IssueAttachment[],
  y: number
): Promise<number> {
  const images = attachments.filter((a) => isImage(a))
  if (images.length === 0) return y
  const gap = 5
  const cellW = (CONTENT_WIDTH - gap) / 2
  const maxH = 62

  for (let i = 0; i < images.length; i += 2) {
    const pair = images.slice(i, i + 2)
    const loaded = await Promise.all(pair.map((a) => loadImageForPdf(a.url)))
    const sizes = loaded.map((l) => {
      if (!l) return { w: cellW, h: 22 }
      let w = cellW
      let h = (cellW * l.h) / l.w
      if (h > maxH) {
        h = maxH
        w = (maxH * l.w) / l.h
      }
      return { w, h }
    })
    const rowH = Math.max(...sizes.map((s) => s.h)) + 5.5
    y = ensureSpace(pdf, y, rowH)

    pair.forEach((att, idx) => {
      const x = MARGIN + idx * (cellW + gap)
      const s = sizes[idx]
      const l = loaded[idx]
      if (l) {
        pdf.addImage(l.data, "JPEG", x, y, s.w, s.h)
        pdf.setDrawColor(229, 231, 235)
        pdf.setLineWidth(0.2)
        pdf.rect(x, y, s.w, s.h, "S")
      } else {
        pdf.setFillColor(243, 244, 246)
        pdf.rect(x, y, s.w, s.h, "F")
        pdf.setFont(KR_FONT, "normal")
        pdf.setFontSize(7.5)
        pdf.setTextColor(156, 163, 175)
        pdf.text("이미지를 불러오지 못했습니다", x + 3, y + s.h / 2 + 1)
      }
      pdf.setFont(KR_FONT, "normal")
      pdf.setFontSize(7)
      pdf.setTextColor(107, 114, 128)
      const caption: string[] = pdf.splitTextToSize(att.name || "첨부 이미지", cellW)
      pdf.text(caption[0] || "", x, y + s.h + 3.4)
    })
    y += rowH
  }
  return y
}

// 비이미지 첨부(문서)는 파일명 목록으로만 기재
function drawFileList(pdf: jsPDF, attachments: IssueAttachment[], y: number): number {
  const files = attachments.filter((a) => !isImage(a))
  if (files.length === 0) return y
  y = ensureSpace(pdf, y, 8)
  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(8.5)
  pdf.setTextColor(107, 114, 128)
  pdf.text("첨부 문서", MARGIN, y + 3.5)
  y += 6
  for (const f of files) {
    y = drawParagraph(pdf, `· ${f.name || "첨부파일"}`, y, {
      size: 8.5,
      color: [107, 114, 128],
      indent: 3,
    })
  }
  return y + 1
}

// ===== 표지 =====
function drawCover(
  pdf: jsPDF,
  equipment: Equipment,
  issues: EquipmentIssue[],
  unitLabelMap: Map<string, string>
) {
  let y = MARGIN + 6

  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(10)
  pdf.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2])
  pdf.text("PAVETECH 장비 이슈 이력서", MARGIN, y)
  y += 12

  pdf.setFontSize(20)
  pdf.setTextColor(17, 24, 39)
  const titleLines: string[] = pdf.splitTextToSize(equipment.productName || "장비", CONTENT_WIDTH)
  pdf.text(titleLines, MARGIN, y)
  y += titleLines.length * 8 + 4

  pdf.setDrawColor(ORANGE[0], ORANGE[1], ORANGE[2])
  pdf.setLineWidth(1)
  pdf.line(MARGIN, y, PAGE_WIDTH - MARGIN, y)
  y += 8

  pdf.setFont(KR_FONT, "normal")
  pdf.setFontSize(10)
  pdf.setTextColor(55, 65, 81)
  const metaRows = [
    `납품업체: ${equipment.supplier || "-"}`,
    `고객사 관리번호: ${equipment.customerMgmtNumber || "-"}`,
    `등록자: ${equipment.creatorName || "-"}`,
    `호기 구성: ${
      (equipment.units || []).length > 0
        ? (equipment.units || []).map((u) => u.unitName).join(", ")
        : "호기 미등록 (공통항목)"
    }`,
    `출력일: ${fmtDate(new Date().toISOString())}`,
  ]
  for (const row of metaRows) {
    const lines: string[] = pdf.splitTextToSize(row, CONTENT_WIDTH)
    pdf.text(lines, MARGIN, y + 4)
    y += lines.length * 5.2 + 1
  }
  y += 6

  const counts: Record<string, number> = { issued: 0, checking: 0, resolved: 0, unavailable: 0 }
  for (const i of issues) {
    if (counts[i.status] !== undefined) counts[i.status] += 1
  }
  const cardW = (CONTENT_WIDTH - 3 * 4) / 4
  const cardH = 18
  const statusKeys = ["issued", "checking", "resolved", "unavailable"]
  statusKeys.forEach((key, idx) => {
    const x = MARGIN + idx * (cardW + 4)
    const rgb = STATUS_RGB[key]
    pdf.setFillColor(249, 250, 251)
    pdf.rect(x, y, cardW, cardH, "F")
    pdf.setFillColor(rgb[0], rgb[1], rgb[2])
    pdf.rect(x, y, cardW, 1.6, "F")
    pdf.setDrawColor(229, 231, 235)
    pdf.setLineWidth(0.2)
    pdf.rect(x, y, cardW, cardH, "S")
    pdf.setFont(KR_FONT, "normal")
    pdf.setFontSize(8.5)
    pdf.setTextColor(107, 114, 128)
    pdf.text(STATUS_LABEL[key], x + 3, y + 7.5)
    pdf.setFont(KR_FONT, "bold")
    pdf.setFontSize(14)
    pdf.setTextColor(17, 24, 39)
    pdf.text(String(counts[key]), x + 3, y + 15)
  })
  y += cardH + 10

  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(12)
  pdf.setTextColor(146, 64, 14)
  pdf.text(`이슈 목록 (총 ${issues.length}건)`, MARGIN, y + 4)
  y += 9

  autoTable(pdf, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [["No", "이슈 제목", "호기", "상태", "등록일"]],
    body: issues.map((issue, idx) => [
      String(idx + 1),
      issue.title?.trim() || "제목없음",
      issue.unitId ? unitLabelMap.get(issue.unitId) || issue.unitLabel || "호기" : "공통항목",
      STATUS_LABEL[issue.status] || issue.status,
      fmtDate(issue.createdAt),
    ]),
    theme: "grid",
    styles: {
      font: KR_FONT,
      fontStyle: "normal",
      fontSize: 8.5,
      cellPadding: 1.8,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
      textColor: [17, 24, 39],
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      font: KR_FONT,
      fontStyle: "bold",
      fillColor: [243, 244, 246],
      textColor: [55, 65, 81],
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 84 },
      2: { cellWidth: 32 },
      3: { cellWidth: 26, halign: "center" },
      4: { cellWidth: 28, halign: "center" },
    },
  })
}

// ===== 이슈 헤더 =====
function drawIssueHeader(
  pdf: jsPDF,
  issue: EquipmentIssue,
  index: number,
  unitLabelMap: Map<string, string>,
  equipmentName: string,
  showIndexBadge: boolean
): number {
  let y = MARGIN
  const rgb = STATUS_RGB[issue.status] || ORANGE

  pdf.setFillColor(rgb[0], rgb[1], rgb[2])
  pdf.rect(MARGIN, y, 3, 16, "F")

  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(9)
  pdf.setTextColor(rgb[0], rgb[1], rgb[2])
  pdf.text(
    showIndexBadge ? `[ISSUE ${String(index + 1).padStart(2, "0")}]` : equipmentName || "장비 이슈",
    MARGIN + 6,
    y + 4.5
  )

  const statusText = STATUS_LABEL[issue.status] || issue.status
  pdf.setFontSize(8.5)
  const badgeW = pdf.getTextWidth(statusText) + 8
  pdf.setFillColor(rgb[0], rgb[1], rgb[2])
  pdf.roundedRect(PAGE_WIDTH - MARGIN - badgeW, y, badgeW, 6.5, 1.5, 1.5, "F")
  pdf.setTextColor(255, 255, 255)
  pdf.text(statusText, PAGE_WIDTH - MARGIN - badgeW + 4, y + 4.5)

  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(14)
  pdf.setTextColor(17, 24, 39)
  const titleLines: string[] = pdf.splitTextToSize(
    issue.title?.trim() || "제목없음",
    CONTENT_WIDTH - 8
  )
  pdf.text(titleLines, MARGIN + 6, y + 12)
  y += 8 + titleLines.length * 6

  pdf.setFont(KR_FONT, "normal")
  pdf.setFontSize(8.5)
  pdf.setTextColor(107, 114, 128)
  const unitText = issue.unitId
    ? unitLabelMap.get(issue.unitId) || issue.unitLabel || "호기"
    : "공통항목"
  const applyText = issue.applyStatus === "applied" ? "적용완료" : "미적용"
  const meta = `${unitText}   |   작성자: ${issue.creatorName || "-"}   |   등록일: ${fmtDateTime(
    issue.createdAt
  )}   |   현장 적용: ${applyText}`
  const metaLines: string[] = pdf.splitTextToSize(meta, CONTENT_WIDTH - 6)
  pdf.text(metaLines, MARGIN + 6, y + 3)
  y += metaLines.length * 4.6 + 2

  const tags = issue.tags || []
  if (tags.length > 0) {
    pdf.setFontSize(8)
    pdf.setTextColor(180, 83, 9)
    const tagLines: string[] = pdf.splitTextToSize(
      tags.map((t) => `#${t}`).join("  "),
      CONTENT_WIDTH - 6
    )
    pdf.text(tagLines, MARGIN + 6, y + 3)
    y += tagLines.length * 4.4 + 1
  }

  const refs = issue.referencedUsers || []
  if (refs.length > 0) {
    pdf.setFont(KR_FONT, "normal")
    pdf.setFontSize(8)
    pdf.setTextColor(107, 114, 128)
    const refLines: string[] = pdf.splitTextToSize(
      `참조: ${refs.map((u) => u.userName).join(", ")}`,
      CONTENT_WIDTH - 6
    )
    pdf.text(refLines, MARGIN + 6, y + 3)
    y += refLines.length * 4.4
  }

  y += 3
  pdf.setDrawColor(rgb[0], rgb[1], rgb[2])
  pdf.setLineWidth(0.6)
  pdf.line(MARGIN, y, PAGE_WIDTH - MARGIN, y)
  return y + 5
}

// ===== 푸터 =====
function drawFooter(pdf: jsPDF, equipment: Equipment, totalIssues: number, singleTitle?: string) {
  const pageCount = pdf.getNumberOfPages()
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p)
    pdf.setDrawColor(229, 231, 235)
    pdf.setLineWidth(0.2)
    pdf.line(MARGIN, PAGE_HEIGHT - 13, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 13)
    pdf.setFont(KR_FONT, "normal")
    pdf.setFontSize(7.5)
    pdf.setTextColor(156, 163, 175)
    const label = singleTitle
      ? `페이브텍 관리 시스템 · ${equipment.productName || "장비"} · ${singleTitle}`
      : `페이브텍 관리 시스템 · ${equipment.productName || "장비"} · 이슈 ${totalIssues}건`
    pdf.text(pdf.splitTextToSize(label, CONTENT_WIDTH - 20)[0] || "", MARGIN, PAGE_HEIGHT - 8)
    pdf.text(`${p} / ${pageCount}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 8, { align: "right" })
  }
}

// ===== 메인 생성 함수 =====
export interface GenerateIssuePdfOptions {
  equipment: Equipment
  issues: EquipmentIssue[]
  comments: EquipmentComment[]
  unitLabelMap: Map<string, string>
  includePhotos?: boolean
  includeComments?: boolean
  /** 표지(장비 정보 + 색인표) 포함 여부. 개별 이슈 출력 시 false */
  withCover?: boolean
  /** 저장 파일명 (확장자 제외) */
  fileName?: string
  onProgress?: (current: number, total: number) => void
}

/** 이슈 PDF를 생성해 즉시 저장한다. 반환값은 출력된 이슈 건수. */
export async function generateEquipmentIssuePdf({
  equipment,
  issues,
  comments,
  unitLabelMap,
  includePhotos = true,
  includeComments = true,
  withCover = true,
  fileName,
  onProgress,
}: GenerateIssuePdfOptions): Promise<number> {
  const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true })
  await ensureKoreanFont(pdf)
  // 폰트 등록 결과 반영 (실패 시 기본 폰트로 폴백)
  KR_FONT = safeFontName()

  const orderedIssues = sortIssuesByStatus(issues)
  const isSingle = !withCover

  if (withCover) {
    drawCover(pdf, equipment, orderedIssues, unitLabelMap)
  }

  for (let idx = 0; idx < orderedIssues.length; idx++) {
    const issue = orderedIssues[idx]
    onProgress?.(idx + 1, orderedIssues.length)
    // 브라우저가 진행률 UI를 그릴 수 있도록 양보
    await new Promise((r) => setTimeout(r, 0))

    // 표지가 있으면 항상 새 페이지, 없으면 첫 이슈는 기본 페이지 사용
    if (withCover || idx > 0) pdf.addPage()
    let y = drawIssueHeader(pdf, issue, idx, unitLabelMap, equipment.productName, !isSingle)

    // --- 구역 1. 이슈 발생 ---
    y = drawSectionBar(pdf, "1. 이슈 발생 내용", y, RED)
    y = drawParagraph(pdf, issue.issueContent || "내용이 등록되지 않았습니다.", y)
    y += 2

    if (issue.expectedCause?.trim() || issue.possibleAction?.trim()) {
      const rows: string[][] = []
      if (issue.expectedCause?.trim()) rows.push(["예상원인 및 History", issue.expectedCause])
      if (issue.possibleAction?.trim()) rows.push(["가능한 조치", issue.possibleAction])
      y = ensureSpace(pdf, y, 18)
      autoTable(pdf, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN },
        body: rows,
        theme: "grid",
        styles: {
          font: KR_FONT,
          fontStyle: "normal",
          fontSize: 8.5,
          cellPadding: 2,
          lineColor: [229, 231, 235],
          lineWidth: 0.1,
          textColor: [55, 65, 81],
          valign: "top",
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 42, fontStyle: "bold", fillColor: [249, 250, 251] },
          1: { cellWidth: CONTENT_WIDTH - 42 },
        },
      })
      // @ts-expect-error - lastAutoTable 은 플러그인이 주입하는 런타임 속성
      y = (pdf.lastAutoTable?.finalY ?? y) + 3
    }

    const issueAtts = issue.attachments || []
    if (includePhotos && issueAtts.length > 0) {
      y = await drawImageGrid(pdf, issueAtts, y)
    }
    y = drawFileList(pdf, issueAtts, y)
    y += 3

    // --- 구역 2. 조치 결과 ---
    y = drawSectionBar(pdf, "2. 조치 결과", y, GREEN)
    if (issue.resolutionContent?.trim()) {
      const infoParts: string[] = []
      if (issue.resolvedByName) infoParts.push(`조치자: ${issue.resolvedByName}`)
      if (issue.resolvedAt) infoParts.push(`조치일: ${fmtDateTime(issue.resolvedAt)}`)
      if (issue.actualCost) infoParts.push(`실제 소요 비용: ${issue.actualCost.toLocaleString()}원`)
      if (infoParts.length > 0) {
        y = drawParagraph(pdf, infoParts.join("   |   "), y, {
          size: 8.5,
          color: [107, 114, 128],
        })
        y += 1
      }
      y = drawParagraph(pdf, issue.resolutionContent, y)
      y += 2
      const resAtts = issue.resolutionAttachments || []
      if (includePhotos && resAtts.length > 0) {
        y = await drawImageGrid(pdf, resAtts, y)
      }
      y = drawFileList(pdf, resAtts, y)
    } else {
      y = drawParagraph(pdf, "아직 조치 결과가 등록되지 않았습니다.", y, {
        size: 9,
        color: [156, 163, 175],
      })
    }

    if (issue.fieldComment?.trim()) {
      y += 2
      y = drawParagraph(pdf, `현장 코멘트: ${issue.fieldComment}`, y, {
        size: 8.5,
        color: [107, 114, 128],
      })
    }
    y += 3

    // --- 구역 3. 코멘트 ---
    if (includeComments) {
      const issueComments = comments
        .filter((c) => c.issueId === issue._id)
        .slice()
        .sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""))
      y = drawSectionBar(pdf, `3. 코멘트 (${issueComments.length}건)`, y, BLUE)
      if (issueComments.length === 0) {
        y = drawParagraph(pdf, "등록된 코멘트가 없습니다.", y, {
          size: 9,
          color: [156, 163, 175],
        })
      } else {
        for (let ci = 0; ci < issueComments.length; ci++) {
          const c = issueComments[ci]
          y = drawCommentCard(
            pdf,
            `${ci + 1}. ${c.creatorName || "-"}  ·  ${fmtDateTime(c.createdAt)}`,
            c.content || "",
            y
          )
          const cAtts = c.attachments || []
          if (includePhotos && cAtts.length > 0) {
            y += 2
            y = await drawImageGrid(pdf, cAtts, y)
          }
          y = drawFileList(pdf, cAtts, y)
          y += 3.5
        }
      }
    }
  }

  drawFooter(
    pdf,
    equipment,
    orderedIssues.length,
    isSingle ? orderedIssues[0]?.title?.trim() || "이슈 상세" : undefined
  )

  const safeName = (fileName || `${equipment.productName || "장비"}_이슈이력`).replace(
    /[\\/:*?"<>|]/g,
    "_"
  )
  const today = new Date().toISOString().slice(0, 10)
  pdf.save(`${safeName}_${today}.pdf`)
  return orderedIssues.length
}
